import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Runtime } from '../runtime';
import { createActions } from '../actions';
import type { Actions } from '../actions';
import type { ConnectionState } from '../mirror/sync';
import type { StaffUser } from '../auth/provider';

/**
 * The binding between the offline layer and React, in about eighty lines.
 *
 * The runtime is not React state. It is a set of plain objects that own
 * IndexedDB and the network, and they announce that something changed by
 * bumping a revision. `useLive` re-runs an async read whenever that happens.
 *
 * The consequence worth naming: **a view cannot read the network.** It calls
 * `useLive(() => mirror.day(today))` and gets whatever the device knows, with no
 * loading state to invent and no way to accidentally make the offline path
 * different from the online one. What a view *can* do is enqueue an intent, and
 * the next revision bump paints the result.
 */

const RuntimeContext = createContext<Runtime | null>(null);

export function RuntimeProvider({
  runtime,
  children,
}: {
  runtime: Runtime;
  children: ReactNode;
}): ReactNode {
  return <RuntimeContext.Provider value={runtime}>{children}</RuntimeContext.Provider>;
}

export function useRuntime(): Runtime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error('useRuntime outside a RuntimeProvider');
  return runtime;
}

export function useActions(): Actions {
  const runtime = useRuntime();
  return useMemo(() => createActions(runtime), [runtime]);
}

/**
 * Run an async read against the mirror, and again on every change.
 *
 * `initial` is what to show before the first read resolves — for the mirror
 * that is a few milliseconds, and for a list the honest placeholder is an empty
 * list rather than a spinner. The spec's staleness rule (§8.1) is about the
 * *age of the data*, which the status strip states plainly; a spinner over a
 * local read would say "waiting for the network", which would be false.
 */
export function useLive<T>(read: () => Promise<T>, initial: T, deps: readonly unknown[] = []): T {
  const runtime = useRuntime();
  const [value, setValue] = useState<T>(initial);
  const readRef = useRef(read);
  readRef.current = read;

  useEffect(() => {
    let alive = true;
    let inFlight = false;
    let again = false;

    const run = (): void => {
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      void readRef
        .current()
        .then((next) => {
          if (alive) setValue(next);
        })
        .catch(() => undefined)
        .finally(() => {
          inFlight = false;
          if (again && alive) {
            again = false;
            run();
          }
        });
    };

    run();
    const unsubscribe = runtime.subscribe(run);
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [runtime, ...deps]);

  return value;
}

export function useConnection(): ConnectionState {
  const runtime = useRuntime();
  const [state, setState] = useState<ConnectionState>(() => runtime.connection());
  useEffect(() => runtime.subscribe(() => setState(runtime.connection())), [runtime]);
  return state;
}

export function useUser(): StaffUser | null {
  const runtime = useRuntime();
  const [user, setUser] = useState<StaffUser | null>(() => runtime.auth.current());
  useEffect(() => runtime.auth.onChange(setUser), [runtime]);
  return user;
}

/** A clock that ticks, so "last synced … 4 min ago" ages without a reload. */
export function useNow(intervalMs = 20_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}
